/**
 * O aviso da recorrência automática de ajuda de custo e a trava do dia nesta
 * aba do navegador.
 *
 * Papel: aqui ficam `avisoDaRecorrencia`, a frase que a tela mostra quando a
 * recorrência gerou algum grupo, e a trava que faz a aba Lançamentos pedir a
 * recorrência uma vez só por dia (`pedirVerificacaoDoDia`).
 *
 * Como usar: a trava é uma variável deste módulo, e vale enquanto a página
 * estiver carregada; recarregar a página a esquece, e o servidor, que tem a
 * trava dele, responde que já verificou. Quem pede e falha chama
 * `desfazerVerificacaoDoDia`, para a próxima abertura tentar de novo. Os testes
 * chamam `desfazerVerificacaoDoDia` antes de cada caso.
 */
import { type ResultadoDaRecorrencia } from '@concierge/shared';

/** O dia em que esta página já pediu a recorrência; `null` antes do primeiro pedido. */
let diaDaVerificacao: string | null = null;

/**
 * Marca o pedido do dia, quando ainda não houve.
 *
 * @param hoje - O dia de hoje, em `AAAA-MM-DD`.
 * @returns Verdadeiro quando é o primeiro pedido do dia, e quem chama deverá
 *   pedir; falso quando já houve.
 */
export function pedirVerificacaoDoDia(hoje: string): boolean {
  if (diaDaVerificacao === hoje) return false;
  diaDaVerificacao = hoje;
  return true;
}

/** Esquece o pedido do dia, para o próximo pedir de novo. */
export function desfazerVerificacaoDoDia(): void {
  diaDaVerificacao = null;
}

/**
 * O aviso da recorrência (l. 5294–5295).
 *
 * @param resultado - O que a rota devolveu.
 * @returns `↻ 2 lançamentos de ajuda de custo gerados automaticamente
 *   (recorrência mensal) (1 corrigido)`, contando grupos como a origem; `null`
 *   quando nada foi criado, e a tela não avisa nada.
 */
export function avisoDaRecorrencia(resultado: ResultadoDaRecorrencia): string | null {
  const { criados, corrigidos } = resultado;
  if (criados <= 0) return null;
  const s = criados > 1 ? 's' : '';
  const reparo =
    corrigidos > 0 ? ` (${String(corrigidos)} corrigido${corrigidos > 1 ? 's' : ''})` : '';
  return `↻ ${String(criados)} lançamento${s} de ajuda de custo gerado${s} automaticamente (recorrência mensal)${reparo}`;
}
